import "./groupCodeStandardSearch.css";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { SIDEBAR_BUTTONS, sidebarGradeValue, useSidebarTracking } from "hooks/useSidebarTracking";
import { Form } from "react-bootstrap";
import { useStandards } from "hooks/useStandards/useStandards";
import { useSemanticStandardSearch } from "hooks/useSemanticStandardSearch";
import { SearchBar } from "components/shared/SearchBar/SearchBar";
import { StandardsFallback } from "components/shared/StandardsFallback/StandardsFallback";
import { StandardNavigationTree } from "./StandardNavigationTree";
import { StandardDetailView } from "./StandardDetailView";
import { SearchResultsView } from "./SearchResultsView";
import { constants, generateRoutePath } from "utils";
import { getGradeLabel, gradeHasVisibleStandards, standardsFrameworkId, expandedNodesForCode } from "utils/standards";
import { useEducatorAccount } from "hooks/useEducatorAccount/useEducatorAccount";
import { useTeacherGradeDefault } from "hooks/useTeacherGradeDefault/useTeacherGradeDefault";
import { withPanelViewMemory } from "hooks/usePageViewTracker";
import logo from "assets/OKO_Core_Logotype2.svg";
import arrow_left_circle from "assets/arrow-left-circle.svg";

const GroupCodeStandardSearchPage = () => {
  const { groupCode } = useParams();
  const history = useHistory();
  const location = useLocation();
  const trackSidebar = useSidebarTracking();
  const standardsData = useStandards(true);
  const { gradeLevels, loaded: educatorLoaded, activeSchool } = useEducatorAccount();
  // A state-framework school (its active school names a framework id) must
  // surface zero-mapped standards so the teacher can pick them and read the
  // zero-count warning; a Common Core school keeps the existing hidden rule.
  const showZeroCountStandards = standardsFrameworkId(activeSchool) !== null;

  const [selectedStandard, setSelectedStandard] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [selectedGrade, setSelectedGrade] = useState("all");
  const [prerequisiteOf, setPrerequisiteOf] = useState(null);
  const mainContentScrollableRef = useRef(null);

  const {
    query: searchQuery,
    results: searchResults,
    isSearching,
    showResults: showSearchResults,
    handleInputChange: handleSearchInputChange,
    handleSubmit: handleSearchSubmit,
    clearSearch,
    inputRef: searchInputRef,
  } = useSemanticStandardSearch();

  // Grades worth offering — those with at least one challenge-bearing standard.
  const availableGrades = useMemo(
    () =>
      (standardsData || [])
        .filter((gradeLevel) => gradeHasVisibleStandards(gradeLevel, showZeroCountStandards))
        .map((gradeLevel) => gradeLevel.grade),
    [standardsData, showZeroCountStandards]
  );

  // Selecting a grade opens it, so the domains are visible without a second click.
  const selectGrade = useCallback((grade) => {
    setSelectedGrade(grade);
    setExpandedNodes((prev) => ({ ...prev, [grade]: true }));
  }, []);

  // First open defaults to the teacher's own grade — the same rule as the lesson
  // and quick-group create flows. A standard or query in the URL is an explicit
  // starting point and wins.
  const urlSelection = new URLSearchParams(location.search);
  const urlHasSelection = urlSelection.has("standard") || urlSelection.has("q");
  useTeacherGradeDefault(gradeLevels, educatorLoaded && !urlHasSelection, availableGrades, selectGrade);

  // Parse URL parameters to restore standard/prereqOf selection.
  // The semantic-search hook owns `q`.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const standardCode = params.get("standard");
    const prereqOfCode = params.get("prereqOf");

    if (standardCode && standardsData.length > 0) {
      // Find and select the standard
      const standard = findStandardByCode(standardsData, standardCode);
      if (standard) {
        setSelectedStandard(standard);
        // Auto-expand the path to this standard
        expandPathToStandard(standardCode);
        // Set the dropdown to the standard's grade
        if (standard.grade) {
          setSelectedGrade(standard.grade);
        }

        // Restore prerequisiteOf from URL
        if (prereqOfCode) {
          const prereqStandard = findStandardByCode(standardsData, prereqOfCode);
          if (prereqStandard) {
            setPrerequisiteOf(prereqStandard);
          }
        } else {
          setPrerequisiteOf(null);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, standardsData]);

  const findStandardByCode = (standards, code) => {
    // Handle different entity types
    if (code.startsWith('grade-')) {
      const gradeValue = code.replace('grade-', '');
      const gradeLevel = standards.find(g => g.grade === gradeValue);
      if (gradeLevel) {
        return {
          type: 'grade',
          grade: gradeLevel.grade,
          domains: gradeLevel.domains
        };
      }
    } else if (code.startsWith('domain-')) {
      const domainPrefix = code.replace('domain-', '');
      for (const gradeLevel of standards) {
        if (gradeLevel.domains) {
          for (const domain of gradeLevel.domains) {
            if (domain.code === domainPrefix) {
              return {
                type: 'domain',
                ...domain,
                grade: gradeLevel.grade
              };
            }
          }
        }
      }
    } else if (code.startsWith('cluster-')) {
      const clusterPrefix = code.replace('cluster-', '');
      for (const gradeLevel of standards) {
        if (gradeLevel.domains) {
          for (const domain of gradeLevel.domains) {
            if (domain.clusters) {
              for (const cluster of domain.clusters) {
                if (cluster.code === clusterPrefix) {
                  return {
                    type: 'cluster',
                    ...cluster,
                    grade: gradeLevel.grade,
                    domain: domain
                  };
                }
              }
            }
          }
        }
      }
    } else {
      // Check if it's a plain grade number (e.g., "6", "K")
      const gradeLevel = standards.find(g => g.grade === code);
      if (gradeLevel) {
        return {
          type: 'grade',
          grade: gradeLevel.grade,
          domains: gradeLevel.domains
        };
      }

      // Check if it's a domain code without prefix (e.g., "6.EE", "5.NF")
      for (const gradeLevel of standards) {
        if (gradeLevel.domains) {
          for (const domain of gradeLevel.domains) {
            if (domain.code === code) {
              return {
                type: 'domain',
                ...domain,
                grade: gradeLevel.grade
              };
            }
          }
        }
      }

      // Check if it's a cluster code without prefix (e.g., "6.EE.A", "5.NF.B")
      for (const gradeLevel of standards) {
        if (gradeLevel.domains) {
          for (const domain of gradeLevel.domains) {
            if (domain.clusters) {
              for (const cluster of domain.clusters) {
                if (cluster.code === code) {
                  return {
                    type: 'cluster',
                    ...cluster,
                    grade: gradeLevel.grade,
                    domain: domain
                  };
                }
              }
            }
          }
        }
      }

      // Standard code
      for (const gradeLevel of standards) {
        if (gradeLevel.domains) {
          for (const domain of gradeLevel.domains) {
            if (domain.clusters) {
              for (const cluster of domain.clusters) {
                if (cluster.standards) {
                  for (const standard of cluster.standards) {
                    if (standard.code === code) {
                      return {
                        ...standard,
                        grade: gradeLevel.grade,
                        domain: domain,
                        cluster: cluster
                      };
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    return null;
  };

  const expandPathToStandard = (entityCode) => {
    // Derived by finding the code in the tree, so any framework's code shapes
    // resolve (the old string parse assumed CCSS and expanded nothing for state
    // codes). Shared with the change-standard view.
    setExpandedNodes(expandedNodesForCode(standardsData, entityCode));
  };

  const handleStandardSelect = (entity, grade, domain, cluster, fromStandard = null) => {
    const entityWithContext = {
      ...entity,
      grade,
      domain,
      cluster
    };
    setSelectedStandard(entityWithContext);

    // Set prerequisiteOf based on fromStandard parameter
    // fromStandard is passed when clicking on a prerequisite
    setPrerequisiteOf(fromStandard);

    // Update URL with the selected entity
    const params = new URLSearchParams(location.search);

    // Determine the entity code based on type
    let entityCode;
    if (entity.type === 'grade') {
      entityCode = `grade-${entity.grade}`;
    } else if (entity.type === 'domain') {
      entityCode = `domain-${entity.code}`;
    } else if (entity.type === 'cluster') {
      entityCode = `cluster-${entity.code}`;
    } else {
      entityCode = entity.code;
    }

    params.set("standard", entityCode);

    // Add prerequisite info to URL if navigating from a prerequisite
    if (fromStandard) {
      params.set("prereqOf", fromStandard.code);
    } else {
      params.delete("prereqOf");
    }

    // Remove search query when viewing a standard
    params.delete("q");
    history.push(`${location.pathname}?${params.toString()}`);
  };

  // Auto-scroll to top when search results change or when a standard is selected
  useEffect(() => {
    if (mainContentScrollableRef.current) {
      // Use requestAnimationFrame to ensure DOM is updated before scrolling
      // This fixes the issue where scroll doesn't work on small resolutions or windowed mode
      requestAnimationFrame(() => {
        if (mainContentScrollableRef.current) {
          mainContentScrollableRef.current.scrollTop = 0;
        }
      });
    }
  }, [showSearchResults, searchResults, selectedStandard]);

  const urlParams = new URLSearchParams(location.search);
  const assignIntent = urlParams.get("intent") === "assign" && urlParams.get("classroomId");
  const classIdFromUrl = urlParams.get("classId");

  const handleReturnToDashboard = () => {
    trackSidebar(SIDEBAR_BUTTONS.BACK_TO_GROUPS);
    const dashboardPath = generateRoutePath(constants.EDUCATOR_ROUTE_NAME);
    // In assign-mode we came from the Phase 1 Groups page (FE-2). Preserve the
    // selected class so the educator lands back on the same classroom view.
    history.push(classIdFromUrl ? `${dashboardPath}?classId=${classIdFromUrl}` : dashboardPath);
  };

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  const handleGradeChange = (e) => {
    const grade = e.target.value;
    trackSidebar(SIDEBAR_BUTTONS.GRADE_FILTER, { grade: sidebarGradeValue(grade) });
    setSelectedGrade(grade);

    // When a specific grade is selected, expand it to show domains
    if (grade !== "all") {
      setExpandedNodes(prev => ({
        ...prev,
        [grade]: true
      }));
    }
  };

  // Filter standards by selected grade
  const filteredStandards = selectedGrade === "all"
    ? standardsData
    : standardsData.filter(grade => grade.grade === selectedGrade);

  if (!standardsData || standardsData.length === 0) {
    return (
      <div className="loading-container">
        <StandardsFallback size="3x" />
      </div>
    );
  }

  return (
    <div className="group-code-standard-search">
      <div className="sidebar">
        <div className="sidebar-header">
          <img src={logo} alt="OKO" className="sidebar-logo" />

          <button className="return-button" onClick={handleReturnToDashboard}>
            <img src={arrow_left_circle} alt="Return" />
            {assignIntent ? "Return to Groups" : "Return to Dashboard"}
          </button>

          <div className="grade-selector">
            <Form.Select
              value={selectedGrade}
              onChange={handleGradeChange}
              className="grade-dropdown"
              aria-label="Filter standards by grade"
            >
              <option value="all">All Grades</option>
              {availableGrades.map(grade => (
                <option key={grade} value={grade}>
                  {getGradeLabel(grade)}
                </option>
              ))}
            </Form.Select>
          </div>
        </div>

        <div className="sidebar-content">
          <StandardNavigationTree
            standards={filteredStandards}
            selectedStandard={selectedStandard}
            expandedNodes={expandedNodes}
            onToggleNode={toggleNode}
            onSelectStandard={handleStandardSelect}
            showZeroCountStandards={showZeroCountStandards}
          />
        </div>
      </div>

      <div className="main-content">
        <div className="content-header">
          <div className="header-left">
            <h1 className="header-title">{constants.GROUP_CODE_STANDARD_SEARCH}</h1>
          </div>
          <div className="header-right">
            <SearchBar
              ref={searchInputRef}
              value={searchQuery}
              onChange={handleSearchInputChange}
              onSubmit={handleSearchSubmit}
              placeholder={"Try: “Multiplying Fractions” or “Proportional relationships”"}
              isLoading={isSearching}
            />
          </div>
        </div>

        <div className="main-content-scrollable" ref={mainContentScrollableRef}>
          {showSearchResults ? (
            <SearchResultsView
              searchResults={searchResults || []}
              onResultClick={handleStandardSelect}
              onBackClick={clearSearch}
              standardsData={standardsData}
              showZeroCountStandards={showZeroCountStandards}
              activeSchool={activeSchool}
            />
          ) : selectedStandard ? (
            <StandardDetailView
              standard={selectedStandard}
              groupCode={groupCode}
              onStandardClick={handleStandardSelect}
              prerequisiteOf={prerequisiteOf}
              showZeroCountStandards={showZeroCountStandards}
            />
          ) : (
            <div className="no-selection">
              <p>Select a standard from the navigation tree or use the search bar to find standards.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const GroupCodeStandardSearch = withPanelViewMemory(GroupCodeStandardSearchPage);
